import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody,
  TableRow, TableCell, CircularProgress, Divider, Alert,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

function Line({ label, amount, indent = 3, bold, color }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: indent, border: 0, py: 0.4, fontWeight: bold ? 700 : 400, color }}>{label}</TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.4, fontWeight: bold ? 700 : 400, color, fontVariantNumeric: "tabular-nums" }}>
        {fmt(amount)}
      </TableCell>
    </TableRow>
  );
}
function SectionHead({ label }) {
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{ border: 0, pt: 1.5, pb: 0.3, fontWeight: 700, fontSize: 14 }}>{label}</TableCell>
    </TableRow>
  );
}
function Subtotal({ label, amount }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: 3, borderTop: "1px solid", borderColor: "divider", py: 0.5, fontWeight: 600 }}>{label}</TableCell>
      <TableCell align="right" sx={{ borderTop: "1px solid", borderColor: "divider", py: 0.5, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(amount)}</TableCell>
    </TableRow>
  );
}
function GrandTotal({ label, amount }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: 2, borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontSize: 15 }}>{label}</TableCell>
      <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>{fmt(amount)}</TableCell>
    </TableRow>
  );
}

export default function BalanceSheet() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    setLoading(true); setError(null);
    api.get("/accounting/balance-sheet", { params: { asOf } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Balance Sheet"))
      .finally(() => setLoading(false));
  }, [asOf]);

  const inBalance = data && Math.abs(data.difference) < 0.005;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Balance Sheet</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        What you own vs. what you owe, as of a date. This is the <b>partial "live" version</b> — built from
        the app's data since you started. Inventory is now valued at weighted-average cost; the remaining
        gap is the <b>opening balances</b> (your position before the app started), coming next.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="As of" InputLabelProps={{ shrink: true }}
            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Balance Sheet</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>As of {data.asOf}</Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ maxWidth: 640, mx: "auto" }}>
              <TableBody>
                {/* ASSETS */}
                <TableRow><TableCell colSpan={2} sx={{ border: 0, pt: 1, pb: 0.3, fontWeight: 800, fontSize: 16 }}>ASSETS</TableCell></TableRow>
                {data.assets.sections.map((s) => (
                  <React.Fragment key={`a-${s.title}`}>
                    <SectionHead label={s.title} />
                    {s.lines.map((l) => <Line key={l.account} label={l.account} amount={l.amount} />)}
                    <Subtotal label={`Total ${s.title}`} amount={s.total} />
                  </React.Fragment>
                ))}
                {data.assets.sections.length === 0 && <Line label="No asset data yet" amount={0} color="text.secondary" />}
                <GrandTotal label="TOTAL ASSETS" amount={data.assets.total} />

                {/* LIABILITIES */}
                <TableRow><TableCell colSpan={2} sx={{ border: 0, pt: 2, pb: 0.3, fontWeight: 800, fontSize: 16 }}>LIABILITIES</TableCell></TableRow>
                {data.liabilities.sections.map((s) => (
                  <React.Fragment key={`l-${s.title}`}>
                    <SectionHead label={s.title} />
                    {s.lines.map((l) => <Line key={l.account} label={l.account} amount={l.amount} />)}
                    <Subtotal label={`Total ${s.title}`} amount={s.total} />
                  </React.Fragment>
                ))}
                {data.liabilities.sections.length === 0 && <Line label="No liability data yet" amount={0} color="text.secondary" />}
                <Subtotal label="Total Liabilities" amount={data.liabilities.total} />

                {/* EQUITY */}
                <TableRow><TableCell colSpan={2} sx={{ border: 0, pt: 2, pb: 0.3, fontWeight: 800, fontSize: 16 }}>EQUITY</TableCell></TableRow>
                {data.equity.section.lines.map((l) => <Line key={l.account} label={l.account} amount={l.amount} />)}
                <Subtotal label="Total Equity" amount={data.equity.total} />

                <GrandTotal label="TOTAL LIABILITIES + EQUITY" amount={data.totalLiabilitiesAndEquity} />
              </TableBody>
            </Table>
          </Box>

          {/* Balance check */}
          <Box sx={{ maxWidth: 640, mx: "auto", mt: 2 }}>
            {inBalance ? (
              <Alert severity="success">In balance — Assets = Liabilities + Equity.</Alert>
            ) : (
              <Alert severity="warning">
                <b>Out of balance by {fmt(data.difference)}.</b> Inventory is now included. The remaining
                gap is the <b>opening balances</b> — your financial position (bank, equity, loans) the day
                before the app started. Once we enter those, this goes to zero.
              </Alert>
            )}
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary", maxWidth: 640, mx: "auto" }}>
            Cash from Daily Cash · Receivable/Payable from the General Ledger · Retained Earnings = profit since
            you started · other accounts from Journal Entries. Fixed assets, loans, and bank balances appear here
            once you record them via Journal Entry (or opening balances).
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
